import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Badge } from "../../components/ui/badge";
import { AlertCircle, Check, LogOut, Megaphone, Package } from "lucide-react";
import { NewsletterTab } from "./views/NewsletterTab";
import { ReviewsTab } from "./views/ReviewsTab";
import { PreordersTab, ReservationsTab } from "./views/PreordersReservationsTabs";
import { ShippingTab, CreateShippingRuleModal } from "./views/ShippingTab";
import { PromosTab } from "./views/PromosTab";
import { CustomersTab } from "./views/CustomersTab";
import { CreateManualOrderModal } from "./views/CreateManualOrderModal";
import { AnalyticsAdvanced } from "./views/AnalyticsAdvanced";
import { DashboardView } from "./views/DashboardView";
import { ArticlesGrid } from "./views/ArticlesGrid";
import { OrdersView } from "./views/OrdersView";
import { ArticleFormDialog } from "./views/ArticleFormDialog";
import { EbayTab } from "./views/EbayTab";
import { SocialTab } from "./views/SocialTab";
import { UtmGeneratorTab } from "./views/UtmGeneratorTab";
import { AiChatConversationsTab } from "./views/AiChatConversationsTab";
import { AdvertisingTab } from "./views/AdvertisingTab";
import { LoginPage } from "./views/LoginPage";
import { ProtectedRoute } from "./views/ProtectedRoute";
import { AnalyticsModal } from "./views/AnalyticsModal";
import { parseEbayError } from "./views/ebayErrorParser";

export { LoginPage, ProtectedRoute };

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;


// Dashboard Component
export const Dashboard = () => {
  const [currentView, setCurrentView] = useState("dashboard"); // dashboard, articles, orders, new-orders, newsletter, shipping, preorders, reservations, reviews, promos, customers
  const [orderFilter, setOrderFilter] = useState("all"); // all, new
  const [articles, setArticles] = useState([]);
  const [orders, setOrders] = useState([]);
  const [newsletterSubscriptions, setNewsletterSubscriptions] = useState([]);
  const [shippingRulesAdmin, setShippingRulesAdmin] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [ivaOptions, setIvaOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [showCreateShippingModal, setShowCreateShippingModal] = useState(false);
  const [showCreateOrderModal, setShowCreateOrderModal] = useState(false);
  const [syncingPacklink, setSyncingPacklink] = useState(false);
  const [packlinkLastSync, setPacklinkLastSync] = useState(null);
  const [duplicateOrderData, setDuplicateOrderData] = useState(null);
  const [editingArticle, setEditingArticle] = useState(null);
  const [ebayLoadingId, setEbayLoadingId] = useState(null);
  const [socialLoadingId, setSocialLoadingId] = useState(null);
  
  // SEO: ensure /gestione is NEVER indexed by Google (even if a sitemap mistake
  // or external backlink exposed it). Injects/updates <meta name="robots"
  // content="noindex,nofollow"> on mount, removes it on unmount.
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex,nofollow,noarchive";
    document.head.appendChild(meta);
    const prevTitle = document.title;
    document.title = "Gestione — Edicola Cerasuolo";
    return () => {
      try { document.head.removeChild(meta); } catch (_) { /* no-op */ }
      document.title = prevTitle;
    };
  }, []);

  // Analytics modal state
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [selectedArticleForAnalytics, setSelectedArticleForAnalytics] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [revenueAnalytics, setRevenueAnalytics] = useState(null);
  const [siteAnalytics, setSiteAnalytics] = useState(null);
  const [wishlistAnalytics, setWishlistAnalytics] = useState(null);
  const [coupons, setCoupons] = useState([]);

  // Memoized derived order lists (avoid recomputing filter/sort on every render)
  const sortedOrdersDesc = useMemo(
    () => [...orders].sort((a, b) => new Date(b.order_date) - new Date(a.order_date)),
    [orders]
  );
  const pendingOrdersDesc = useMemo(
    () => sortedOrdersDesc.filter(o => o.status === 'pending'),
    [sortedOrdersDesc]
  );
  const recent5OrdersDesc = useMemo(
    () => sortedOrdersDesc.slice(0, 5),
    [sortedOrdersDesc]
  );
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState("");
  const [customerOrders, setCustomerOrders] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [newsletterStats, setNewsletterStats] = useState(null);
  
  const { user, logout } = useAuth();

  // Form state
  const [formData, setFormData] = useState({
    sku: "",
    title: "",
    description: "",
    price: "",
    iva_percentage: "0",
    quantity_stock: "",
    shipping_cost_national: "",
    shipping_cost_international: "",
    category: "",
    weight: "",
    dimensions: "",
    availability_type: "available",
    preorder_date: "",
    reservation_info: "",
    publication_channel: "both",
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // View dispatcher: fetches the relevant data when the active tab changes.
  // Functions referenced (fetchArticles, fetchOrders, …) are component-local
  // closures over setState setters (stable). No stale-closure risk in practice.
  // Wrapping each in useCallback would touch ~100 lines for no behavioral gain.
  useEffect(() => {
    if (currentView === "articles") {
      fetchArticles();
      fetchIvaOptions();
    } else if (currentView === "orders") {
      fetchOrders();
      fetchShippingRules(); // Load shipping rules for country display
      // Fetch last Packlink sync timestamp for the orders header widget
      axios.get(`${API}/packlink/last-sync`)
        .then(res => setPacklinkLastSync(res.data))
        .catch(() => {});
    } else if (currentView === "newsletter") {
      fetchNewsletterSubscriptions();
      fetchNewsletterStats();
    } else if (currentView === "shipping") {
      fetchShippingRules();
    } else if (currentView === "reviews") {
      fetchReviews();
    } else if (currentView === "promos") {
      fetchCoupons();
    } else if (currentView === "customers") {
      fetchCustomers();
    } else if (currentView === "preorders" || currentView === "reservations") {
      fetchArticles();
    } else if (currentView === "dashboard") {
      fetchDashboardData();
    }
  }, [currentView]);

  const fetchDashboardData = async () => {
    try {
      setError("");
      
      // Ensure token is set in axios headers
      const token = sessionStorage.getItem('access_token');
      if (token && !axios.defaults.headers.common['Authorization']) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
      
      const requests = [
        axios.get(`${API}/articles`, { timeout: 15000 }),
        axios.get(`${API}/orders`, { timeout: 15000 }),
        axios.get(`${API}/newsletter/subscriptions`, { timeout: 15000 }),
        axios.get(`${API}/dashboard/revenue-analytics`, { timeout: 15000 }),
        axios.get(`${API}/dashboard/site-analytics`, { timeout: 15000 }),
        axios.get(`${API}/dashboard/wishlist-analytics`, { timeout: 15000 }),
        axios.get(`${API}/customers`, { timeout: 15000 }),
      ];
      const results = await Promise.allSettled(requests);
      const [articlesRes, ordersRes, newsletterRes, revenueRes, siteRes, wishlistRes, customersRes] = results;

      if (articlesRes.status === "fulfilled") setArticles(articlesRes.value.data);
      if (ordersRes.status === "fulfilled") setOrders(ordersRes.value.data);
      if (newsletterRes.status === "fulfilled") setNewsletterSubscriptions(newsletterRes.value.data);
      if (revenueRes.status === "fulfilled") setRevenueAnalytics(revenueRes.value.data);
      if (siteRes.status === "fulfilled") setSiteAnalytics(siteRes.value.data);
      if (wishlistRes.status === "fulfilled") setWishlistAnalytics(wishlistRes.value.data);
      if (customersRes.status === "fulfilled") setCustomers(customersRes.value.data);

      const unavailable = results.filter((result) => result.status === "rejected").length;
      if (unavailable) {
        console.error("Dashboard requests unavailable:", results);
        setError(unavailable === results.length
          ? "Il gestionale non riesce a raggiungere il server. Riprova tra qualche secondo."
          : "Alcuni dati non sono disponibili al momento. Puoi continuare a usare il gestionale.");
      }
      
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
      setError("Errore parziale nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  };

  const fetchNewsletterSubscriptions = async () => {
    try {
      setLoading(true);
      // Ensure token is set in axios headers
      const token = sessionStorage.getItem('access_token');
      if (token && !axios.defaults.headers.common['Authorization']) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
      const response = await axios.get(`${API}/newsletter/subscriptions`);
      setNewsletterSubscriptions(response.data);
      setError("");
    } catch (err) {
      console.error("Error fetching newsletter subscriptions:", err);
      setError("Errore nel caricamento delle iscrizioni");
    } finally {
      setLoading(false);
    }
  };


  const fetchCoupons = async () => {
    try {
      const response = await axios.get(`${API}/coupons`);
      setCoupons(response.data);
    } catch (error) {
      console.error('Error fetching coupons:', error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`);
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchCustomerOrders = async (customerId) => {
    try {
      const response = await axios.get(`${API}/customers/${customerId}/orders`);
      setCustomerOrders(response.data);
    } catch (error) {
      console.error('Error fetching customer orders:', error);
    }
  };

  const fetchNewsletterStats = async () => {
    try {
      const response = await axios.get(`${API}/newsletter/stats`);
      setNewsletterStats(response.data);
    } catch (error) {
      console.error('Error fetching newsletter stats:', error);
    }
  };

  const fetchShippingRules = async () => {
    try {
      // Don't block UI
      const response = await axios.get(`${API}/shipping-rules`);
      setShippingRulesAdmin(response.data.shipping_rules || []);
      setError("");
    } catch (err) {
      console.error("Error fetching shipping rules:", err);
      setError("Errore nel caricamento delle regole di spedizione");
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      // Don't block UI
      const response = await axios.get(`${API}/reviews`);
      setReviews(response.data.reviews || []);
      setError("");
    } catch (err) {
      console.error("Error fetching reviews:", err);
      setError("Errore nel caricamento delle recensioni");
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async () => {
    try {
      // Don't block UI
      // Ensure token is set in axios headers
      const token = sessionStorage.getItem('access_token');
      if (token && !axios.defaults.headers.common['Authorization']) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
      const response = await axios.get(`${API}/orders`);
      setOrders(response.data);
      setError("");
    } catch (err) {
      console.error("Error fetching orders:", err);
      setError("Errore nel caricamento degli ordini");
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicateOrder = (order) => {
    setDuplicateOrderData({
      customer_name: order.customer_info?.name || "",
      customer_email: order.customer_info?.email || "",
      customer_phone: order.customer_info?.phone || "",
      customer_address: order.customer_info?.address || "",
      customer_city: order.customer_info?.city || "",
      customer_postal_code: order.customer_info?.zipCode || "",
      customer_country: order.customer_info?.specificCountry || order.customer_info?.country || "Italia",
      payment_method: order.payment_method || "cash",
      shipping_cost: order.shipping_cost || 0,
      notes: order.customer_info?.notes || "",
      items: (order.items || []).map(item => ({
        article_id: item.article_id,
        quantity: item.quantity || 1
      }))
    });
    setShowCreateOrderModal(true);
  };

  const updateOrderStatus = async (orderId, newStatus, trackingNumber = null, opts = {}) => {
    try {
      const params = new URLSearchParams();
      params.append('status', newStatus);
      if (trackingNumber) {
        params.append('tracking_number', trackingNumber);
      }
      // Optional cancellation reason + send_email flag
      if (opts.reason && String(opts.reason).trim()) {
        params.append('reason', String(opts.reason).trim());
      }
      if (typeof opts.sendEmail === 'boolean') {
        params.append('send_email', String(opts.sendEmail));
      }

      await axios.put(`${API}/orders/${orderId}/status?${params.toString()}`);
      
      setSuccess(`Stato ordine aggiornato a: ${newStatus}`);
      fetchOrders(); // Refresh orders
      
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("Error updating order status:", err);
      setError("Errore nell'aggiornamento dello stato ordine");
    }
  };

  const fetchArticles = async () => {
    try {
      const response = await axios.get(`${API}/articles`, { timeout: 15000 });
      setArticles(response.data);
      setError("");
    } catch (err) {
      console.error("Error fetching articles:", err);
      setError("Errore nel caricamento degli articoli");
    } finally {
      setLoading(false);
    }
  };

  const fetchIvaOptions = async () => {
    try {
      const response = await axios.get(`${API}/config/iva-options`);
      setIvaOptions(response.data);
    } catch (err) {
      console.error("Error fetching IVA options:", err);
    }
  };

  const fetchArticleAnalytics = async (articleId) => {
    setLoadingAnalytics(true);
    try {
      const response = await axios.get(`${API}/articles/${articleId}/analytics`);
      setAnalyticsData(response.data);
    } catch (err) {
      console.error("Error fetching analytics:", err);
      setError("Errore nel caricamento delle statistiche");
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const openAnalyticsModal = (article) => {
    setSelectedArticleForAnalytics(article);
    setShowAnalyticsModal(true);
    fetchArticleAnalytics(article.id);
  };

  const resetForm = () => {
    setFormData({
      sku: "",
      title: "",
      description: "",
      price: "",
      iva_percentage: "0",
      quantity_stock: "",
      shipping_cost_national: "",
      shipping_cost_international: "",
      category: "",
      weight: "",
      dimensions: "",
      availability_type: "available",
      preorder_date: "",
      reservation_info: "",
      publication_channel: "both",
    });
    setSelectedFile(null);
    setEditingArticle(null);
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) fileInput.value = '';
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({
      ...prev,
      iva_percentage: value
    }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (files.length > 5) {
      setError("Massimo 5 foto per articolo.");
      return;
    }
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    for (const f of files) {
      if (!allowedTypes.includes(f.type)) {
        setError(`Tipo file non supportato: ${f.name}. Usa JPEG, PNG, GIF o WebP.`);
        return;
      }
      if (f.size > 5 * 1024 * 1024) {
        setError(`File "${f.name}" troppo grande. Massimo 5MB ciascuno.`);
        return;
      }
    }
    setSelectedFile(files);
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.sku.trim() || !formData.title.trim() || !formData.description.trim()) {
      setError("SKU, titolo e descrizione sono obbligatori");
      return;
    }

    try {
      setUploading(true);
      setError("");
      
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== "") {
          formDataToSend.append(key, formData[key]);
        }
      });
      
      // Add newsletter toggle value (only for new articles)
      if (!editingArticle) {
        const sendNewsletter = document.getElementById('sendNewsletter')?.checked ?? true;
        formDataToSend.append("send_newsletter", sendNewsletter.toString());

        // The selected channel is the single source of truth for website/eBay.
        formDataToSend.append("publish_to_ebay", ["both", "ebay"].includes(formData.publication_channel) ? "true" : "false");

        // Social (FB + IG) publish toggle (only meaningful at create time)
        const publishToSocial = document.getElementById('publishToSocial')?.checked ?? false;
        formDataToSend.append("publish_to_social", publishToSocial.toString());
      }
      // eBay per-article overrides (category, condition) — editable both on create AND edit
      // so admin can fix invalid-category errors after initial save
      const ebayCatId = document.getElementById('ebay_category_id')?.value?.trim() || "";
      if (ebayCatId) formDataToSend.append("ebay_category_id", ebayCatId);
      const ebayCondition = document.getElementById('ebay_condition')?.value || "";
      if (ebayCondition) formDataToSend.append("ebay_condition", ebayCondition);
      // ebay_price is editable both on create AND edit
      const ebayPriceVal = document.getElementById('ebay_price')?.value?.trim();
      if (ebayPriceVal) formDataToSend.append("ebay_price", ebayPriceVal);
      // EAN / barcode — always send (even if empty) so the backend can clear it on edit
      const eanVal = document.getElementById('ean')?.value?.trim() || "";
      formDataToSend.append("ean", eanVal);
      // eBay Item Specifics: parse "Key: Value" lines into a JSON dict.
      // Always send (even empty string) to allow clearing on update.
      const aspectsRaw = document.getElementById('ebay_aspects')?.value || "";
      const aspectsDict = {};
      aspectsRaw.split('\n').forEach((line) => {
        const idx = line.indexOf(':');
        if (idx < 1) return;
        const k = line.slice(0, idx).trim();
        const v = line.slice(idx + 1).trim();
        if (k && v) aspectsDict[k] = v;
      });
      formDataToSend.append("ebay_aspects", JSON.stringify(aspectsDict));
      
      if (selectedFile) {
        const files = Array.isArray(selectedFile) ? selectedFile : [selectedFile];
        files.forEach((f) => formDataToSend.append("photos", f));
        // legacy single-photo field kept for backwards compatibility (admin
        // endpoint still accepts `photo` as the first image).
        if (files[0]) formDataToSend.append("photo", files[0]);
      }

      let response;
      const token = sessionStorage.getItem('access_token');
      const headers = {
        'Content-Type': 'multipart/form-data',
        ...(token && { 'Authorization': `Bearer ${token}` })
      };
      
      if (editingArticle) {
        response = await axios.put(`${API}/articles/${editingArticle.id}`, formDataToSend, {
          headers,
        });
        setSuccess("Articolo aggiornato con successo!");
      } else {
        response = await axios.post(`${API}/articles`, formDataToSend, {
          headers,
        });
        setSuccess("Articolo creato con successo!");
      }

      resetForm();
      setIsCreateDialogOpen(false);
      await fetchArticles();
      
    } catch (err) {
      console.error("Error saving article:", err);
      const errorMsg = err.response?.data?.detail;
      setError(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg) || "Errore nel salvataggio dell'articolo");
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (article) => {
    setFormData({
      sku: article.sku,
      title: article.title,
      description: article.description,
      price: article.price.toString(),
      iva_percentage: article.iva_percentage,
      quantity_stock: article.quantity_stock.toString(),
      shipping_cost_national: article.shipping_cost_national.toString(),
      shipping_cost_international: article.shipping_cost_international.toString(),
      category: article.category || "",
      weight: article.weight ? article.weight.toString() : "",
      dimensions: article.dimensions || "",
      availability_type: article.availability_type || "available",
      preorder_date: article.preorder_date || "",
      reservation_info: article.reservation_info || "",
    });
    setEditingArticle(article);
    setIsCreateDialogOpen(true);
  };

  const handleDelete = async (articleId) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo articolo?")) {
      return;
    }

    try {
      await axios.delete(`${API}/articles/${articleId}`);
      setSuccess("Articolo eliminato con successo!");
      await fetchArticles();
    } catch (err) {
      console.error("Error deleting article:", err);
      const errorMsg = err.response?.data?.detail;
      setError(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg) || "Errore nell'eliminazione dell'articolo");
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getIvaLabel = (percentage) => {
    const option = ivaOptions.find(opt => opt.value === percentage);
    return option ? option.label : `${percentage}%`;
  };

  const getPaymentMethodLabel = (method) => {
    const methods = {
      'cash': 'Contrassegno',
      'transfer': 'Bonifico Bancario',
      'paypal': 'PayPal',
      'card': 'Carta di Credito'
    };
    return methods[method] || method;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Caricamento magazzino...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      {/* Header */}
      <header className="bg-black/40 border-b border-white/10 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 py-4 flex flex-wrap gap-4 justify-between items-center">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-amber-500 rounded-lg">
              <Package className="w-6 h-6 text-black" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Edicola Cerasuolo</h1>
              <p className="text-sm text-gray-400">Sistema Gestione Magazzino</p>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-4 items-center min-w-0">
            <span className="text-sm text-gray-400">
              Benvenuto
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={logout}
              className="flex items-center space-x-2 bg-transparent border-white/20 text-gray-200 hover:bg-white/10"
            >
              <LogOut className="w-4 h-4" />
              <span>Esci</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Navigation Tabs */}
        <div className="flex flex-wrap gap-1 mb-8 border-b border-white/10" data-testid="admin-navigation-tabs">
          <button
            onClick={() => setCurrentView("dashboard")}
            className={`px-4 py-2 font-medium whitespace-nowrap ${
              currentView === "dashboard"
                ? "text-amber-400 border-b-2 border-amber-400"
                : "text-gray-400 hover:text-gray-200"
            }`}
          >
            📊 Dashboard
          </button>
          <button
            onClick={() => setCurrentView("articles")}
            data-testid="admin-articles-tab"
            className={`px-4 py-2 font-medium whitespace-nowrap ${
              currentView === "articles"
                ? "text-amber-400 border-b-2 border-amber-400"
                : "text-gray-400 hover:text-gray-200"
            }`}
          >
            📦 Articoli ({articles.length})
          </button>
          <button
            onClick={() => {
              setCurrentView("orders");
              setOrderFilter("all");
            }}
            className={`px-4 py-2 font-medium whitespace-nowrap relative ${
              currentView === "orders" && orderFilter === "all"
                ? "text-amber-400 border-b-2 border-amber-400"
                : "text-gray-400 hover:text-gray-200"
            }`}
          >
            🛒 Ordini ({orders.length})
          </button>
          <button
            onClick={() => {
              setCurrentView("orders");
              setOrderFilter("new");
            }}
            className={`px-4 py-2 font-medium whitespace-nowrap relative ${
              currentView === "orders" && orderFilter === "new"
                ? "text-amber-400 border-b-2 border-amber-400"
                : "text-gray-400 hover:text-gray-200"
            }`}
          >
            🆕 Nuovi Ordini ({orders.filter(o => o.status === 'pending').length})
            {orders.filter(o => o.status === 'pending').length > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                {orders.filter(o => o.status === 'pending').length}
              </span>
            )}
          </button>
          <button
            onClick={() => {
              setCurrentView("orders");
              setOrderFilter("confirmed");
            }}
            className={`px-4 py-2 font-medium whitespace-nowrap relative ${
              currentView === "orders" && orderFilter === "confirmed"
                ? "text-amber-400 border-b-2 border-amber-400"
                : "text-gray-400 hover:text-gray-200"
            }`}
          >
            ✅ Da Gestire ({orders.filter(o => o.status === 'confirmed' || o.status === 'processing' || o.status === 'partially_shipped').length})
          </button>
          <button
            onClick={() => {
              setCurrentView("orders");
              setOrderFilter("shipped");
            }}
            className={`px-4 py-2 font-medium whitespace-nowrap relative ${
              currentView === "orders" && orderFilter === "shipped"
                ? "text-amber-400 border-b-2 border-amber-400"
                : "text-gray-400 hover:text-gray-200"
            }`}
          >
            📦 Spediti ({orders.filter(o => o.status === 'shipped').length})
          </button>
          <button
            onClick={() => {
              setCurrentView("orders");
              setOrderFilter("delivered");
            }}
            className={`px-4 py-2 font-medium whitespace-nowrap relative ${
              currentView === "orders" && orderFilter === "delivered"
                ? "text-amber-400 border-b-2 border-amber-400"
                : "text-gray-400 hover:text-gray-200"
            }`}
          >
            ✓ Consegnati ({orders.filter(o => o.status === 'delivered').length})
          </button>
          <button
            onClick={() => setCurrentView("newsletter")}
            className={`px-4 py-2 font-medium whitespace-nowrap ${
              currentView === "newsletter"
                ? "text-amber-400 border-b-2 border-amber-400"
                : "text-gray-400 hover:text-gray-200"
            }`}
          >
            📧 Newsletter ({newsletterSubscriptions.length})
          </button>
          <button
            onClick={() => setCurrentView("shipping")}
            data-testid="admin-shipping-tab"
            className={`px-4 py-2 font-medium whitespace-nowrap ${
              currentView === "shipping"
                ? "text-amber-400 border-b-2 border-amber-400"
                : "text-gray-400 hover:text-gray-200"
            }`}
          >
            🚚 Spedizioni
          </button>
          <button
            onClick={() => setCurrentView("advertising")}
            data-testid="admin-advertising-tab"
            className={`px-4 py-2 font-medium whitespace-nowrap ${
              currentView === "advertising"
                ? "text-amber-400 border-b-2 border-amber-400"
                : "text-gray-400 hover:text-gray-200"
            }`}
          >
            <Megaphone className="mr-2 inline h-4 w-4" /> Pubblicità
          </button>
          <button
            onClick={() => setCurrentView("preorders")}
            className={`px-4 py-2 font-medium whitespace-nowrap ${
              currentView === "preorders"
                ? "text-amber-400 border-b-2 border-amber-400"
                : "text-gray-400 hover:text-gray-200"
            }`}
          >
            🚀 Prevendita ({articles.filter(a => a.availability_type === 'preorder').length})
          </button>
          <button
            onClick={() => setCurrentView("reservations")}
            className={`px-4 py-2 font-medium whitespace-nowrap ${
              currentView === "reservations"
                ? "text-amber-400 border-b-2 border-amber-400"
                : "text-gray-400 hover:text-gray-200"
            }`}
          >
            📋 Prenotazione ({articles.filter(a => a.availability_type === 'reservation').length})
          </button>
          <button
            onClick={() => setCurrentView("reviews")}
            className={`px-4 py-2 font-medium whitespace-nowrap ${
              currentView === "reviews"
                ? "text-amber-400 border-b-2 border-amber-400"
                : "text-gray-400 hover:text-gray-200"
            }`}
          >
            ⭐ Recensioni
          </button>
          <button
            onClick={() => setCurrentView("promos")}
            data-testid="admin-promos-tab"
            className={`px-4 py-2 font-medium whitespace-nowrap ${
              currentView === "promos"
                ? "text-amber-400 border-b-2 border-amber-400"
                : "text-gray-400 hover:text-gray-200"
            }`}
          >
            🏷️ Promozioni
          </button>
          <button
            onClick={() => setCurrentView("customers")}
            className={`px-4 py-2 font-medium whitespace-nowrap ${
              currentView === "customers"
                ? "text-amber-400 border-b-2 border-amber-400"
                : "text-gray-400 hover:text-gray-200"
            }`}
            data-testid="admin-customers-tab"
          >
            👥 Clienti ({customers.length})
          </button>
          <button
            onClick={() => setCurrentView("ebay")}
            className={`px-4 py-2 font-medium whitespace-nowrap ${
              currentView === "ebay"
                ? "text-amber-400 border-b-2 border-amber-400"
                : "text-gray-400 hover:text-gray-200"
            }`}
            data-testid="admin-ebay-tab"
          >
            🟦 eBay
          </button>
          <button
            onClick={() => setCurrentView("social")}
            className={`px-4 py-2 font-medium whitespace-nowrap ${
              currentView === "social"
                ? "text-amber-400 border-b-2 border-amber-400"
                : "text-gray-400 hover:text-gray-200"
            }`}
            data-testid="admin-social-tab"
          >
            📣 Social
          </button>
          <button
            onClick={() => setCurrentView("utm")}
            className={`px-4 py-2 font-medium whitespace-nowrap ${
              currentView === "utm"
                ? "text-amber-400 border-b-2 border-amber-400"
                : "text-gray-400 hover:text-gray-200"
            }`}
            data-testid="admin-utm-tab"
          >
            🔗 Link UTM
          </button>
          <button
            onClick={() => setCurrentView("ai-chat")}
            className={`px-4 py-2 font-medium whitespace-nowrap ${
              currentView === "ai-chat"
                ? "text-amber-400 border-b-2 border-amber-400"
                : "text-gray-400 hover:text-gray-200"
            }`}
            data-testid="admin-ai-chat-tab"
          >
            💬 Chat AI
          </button>
        </div>

        {/* Action Bar */}
        {currentView === "articles" && (
          <div className="flex justify-between items-center mb-8">
            <div>
              <h2 className="text-2xl font-bold text-white">Gestione Articoli</h2>
              <p className="text-gray-600">Totale articoli: {articles.length}</p>
            </div>
            
            <ArticleFormDialog
              isOpen={isCreateDialogOpen}
              setIsOpen={setIsCreateDialogOpen}
              editingArticle={editingArticle}
              formData={formData}
              ivaOptions={ivaOptions}
              selectedFile={selectedFile}
              uploading={uploading}
              newsletterSubscriptions={newsletterSubscriptions}
              onSubmit={handleSubmit}
              onInputChange={handleInputChange}
              onSelectChange={handleSelectChange}
              onFileChange={handleFileChange}
              onFilesReplaced={(files) => setSelectedFile(files)}
              onImagesReordered={(newOrder) => {
                // Reflect the new order in the in-memory editingArticle so the
                // gallery and main preview update without closing the dialog.
                setEditingArticle((prev) => prev ? { ...prev, images_url: newOrder, image: newOrder[0], photo_url: newOrder[0] } : prev);
                // Also refresh the articles list so the grid card thumbnail follows.
                fetchArticles();
              }}
              resetForm={resetForm}
            />
        </div>
        )}

        {/* Alerts */}
        {error && (
          <Alert className="mb-6 flex flex-wrap items-center justify-between gap-3 border-red-200 bg-red-50" data-testid="admin-error-alert">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800">{error}</AlertDescription>
            {currentView === "dashboard" && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setLoading(true);
                  fetchDashboardData();
                }}
                data-testid="admin-dashboard-retry-button"
              >
                Riprova
              </Button>
            )}
          </Alert>
        )}

        {success && (
          <Alert className="mb-6 border-green-200 bg-green-50">
            <Check className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800">{success}</AlertDescription>
          </Alert>
        )}

        {/* Articles Grid */}
        {currentView === "articles" && (
          <>
            {(() => {
              const failedCount = articles.filter(a => a?.ebay?.status === "error").length;
              if (failedCount === 0) return null;
              return (
                <div className="mb-4 flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="ebay-failed-banner">
                  <div className="text-sm text-amber-900">
                    ⚠️ <b>{failedCount}</b> articol{failedCount === 1 ? "o" : "i"} con errore eBay. Correggi categoria/condizione, poi clicca →
                  </div>
                  <button
                    type="button"
                    disabled={ebayLoadingId === "__bulk__"}
                    onClick={async () => {
                      if (!window.confirm(`Tentare di ripubblicare ${failedCount} articol${failedCount === 1 ? "o" : "i"} in errore su eBay?\n\nAssicurati di aver corretto categoria/condizione prima.`)) return;
                      setEbayLoadingId("__bulk__");
                      try {
                        const token = sessionStorage.getItem("access_token");
                        const r = await axios.post(`${API}/ebay/republish-failed`, {}, {
                          headers: { Authorization: `Bearer ${token}` },
                        });
                        const d = r.data;
                        if (d.ok > 0 || d.skipped > 0) {
                          setSuccess(`🔁 Ripubblicazione completata: ${d.ok} OK · ${d.skipped} skip · ${d.failed} ancora in errore (totale ${d.total}).`);
                        } else {
                          setError(`Ripubblicazione: ${d.failed}/${d.total} ancora in errore. Controlla categoria/condizione articolo per articolo.`);
                        }
                        await fetchArticles();
                      } catch (err) {
                        setError(parseEbayError(err.response?.data?.detail || err.message));
                      } finally {
                        setEbayLoadingId(null);
                      }
                    }}
                    className="bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded whitespace-nowrap"
                    data-testid="ebay-republish-failed-btn"
                  >
                    {ebayLoadingId === "__bulk__" ? "⏳ In corso..." : "🔁 Ripubblica tutti gli errori"}
                  </button>
                </div>
              );
            })()}
            <ArticlesGrid
            articles={articles}
            backendUrl={BACKEND_URL}
            formatPrice={formatPrice}
            getIvaLabel={getIvaLabel}
            onAnalytics={openAnalyticsModal}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onShare={(article) => {
              const shareUrl = `${window.location.origin}/prodotto/${article.slug || article.id}`;
              const shareText = `🛒 ${article.title}\n💰 ${formatPrice(article.price)}\n\n${shareUrl}`;
              navigator.clipboard.writeText(shareUrl).then(() => {
                alert(`✅ Link copiato!\n\n${shareText}\n\nPuoi incollarlo su WhatsApp, Facebook, Instagram o dove vuoi!`);
              });
            }}
            ebayLoadingId={ebayLoadingId}
            onPublishEbay={async (article) => {
              if (!window.confirm(`Pubblicare/aggiornare "${article.title}" su eBay?`)) return;
              setEbayLoadingId(article.id);
              try {
                const token = sessionStorage.getItem("access_token");
                const r = await axios.post(
                  `${API}/ebay/publish/${article.id}`,
                  {},
                  { headers: { Authorization: `Bearer ${token}` } }
                );
                if (r.data.ok) {
                  setSuccess(`✅ Articolo su eBay! Offer ID: ${r.data.offer_id} · Listing ID: ${r.data.listing_id || "—"}`);
                  await fetchArticles();
                } else {
                  setError(parseEbayError(r.data.error, article));
                }
              } catch (err) {
                setError(parseEbayError(err.response?.data?.detail || err.message, article));
              } finally {
                setEbayLoadingId(null);
              }
            }}
            onEndEbay={async (article) => {
              if (!window.confirm(`Ritirare il listing eBay di "${article.title}"?`)) return;
              setEbayLoadingId(article.id);
              try {
                const token = sessionStorage.getItem("access_token");
                await axios.post(
                  `${API}/ebay/end/${article.id}`,
                  {},
                  { headers: { Authorization: `Bearer ${token}` } }
                );
                setSuccess(`✅ Listing eBay ritirato`);
                await fetchArticles();
              } catch (err) {
                setError(`Errore: ${err.response?.data?.detail || err.message}`);
              } finally {
                setEbayLoadingId(null);
              }
            }}
            onReconcileEbay={async (article) => {
              setEbayLoadingId(article.id);
              try {
                const token = sessionStorage.getItem("access_token");
                const r = await axios.post(
                  `${API}/ebay/reconcile-stock/${article.id}`,
                  {},
                  { headers: { Authorization: `Bearer ${token}` } }
                );
                const d = r.data || {};
                if (d.old_stock === d.new_stock) {
                  setSuccess(`✅ Stock già allineato: ${d.new_stock} (nessun cambio necessario)`);
                } else {
                  setSuccess(`✅ Stock riallineato: ${d.old_stock} → ${d.new_stock} (eBay attuale)`);
                }
                await fetchArticles();
              } catch (err) {
                setError(`Errore riallineamento: ${err.response?.data?.detail || err.message}`);
              } finally {
                setEbayLoadingId(null);
              }
            }}
            socialLoadingId={socialLoadingId}
            onPublishSocial={async (article) => {
              if (!window.confirm(`Pubblicare "${article.title}" su Facebook + Instagram?`)) return;
              setSocialLoadingId(article.id);
              try {
                const token = sessionStorage.getItem("access_token");
                const r = await axios.post(
                  `${API}/admin/social/publish-product`,
                  { article_id: article.id, to_facebook: true, to_instagram: true },
                  { headers: { Authorization: `Bearer ${token}` } }
                );
                const fb = r.data?.results?.facebook;
                const ig = r.data?.results?.instagram;
                const fbOk = fb?.success;
                const igOk = ig?.success;
                if (fbOk && igOk) {
                  setSuccess(`✅ Pubblicato su Facebook + Instagram!`);
                } else if (fbOk) {
                  setError(`📘 FB ✅ · 📸 IG ❌: ${ig?.error || "errore sconosciuto"}`);
                } else if (igOk) {
                  setError(`📘 FB ❌: ${fb?.error || "errore"} · 📸 IG ✅`);
                } else {
                  setError(`Pubblicazione fallita: FB=${fb?.error || "?"}, IG=${ig?.error || "?"}`);
                }
              } catch (err) {
                setError(`Errore pubblicazione social: ${err.response?.data?.detail || err.message}`);
              } finally {
                setSocialLoadingId(null);
              }
            }}
          />
          </>
        )}
        
        {/* Orders Management */}
        {currentView === "orders" && (
          <OrdersView
            orders={orders}
            orderFilter={orderFilter}
            packlinkLastSync={packlinkLastSync}
            setPacklinkLastSync={setPacklinkLastSync}
            syncingPacklink={syncingPacklink}
            setSyncingPacklink={setSyncingPacklink}
            setSuccess={setSuccess}
            setError={setError}
            fetchOrders={fetchOrders}
            setShowCreateOrderModal={setShowCreateOrderModal}
            formatDate={formatDate}
            formatPrice={formatPrice}
            getPaymentMethodLabel={getPaymentMethodLabel}
            shippingRulesAdmin={shippingRulesAdmin}
            updateOrderStatus={updateOrderStatus}
            handleDuplicateOrder={handleDuplicateOrder}
          />
        )}

        {/* Dashboard Overview */}
        {currentView === "dashboard" && (
          <DashboardView
            loading={loading}
            articles={articles}
            orders={orders}
            newsletterSubscriptions={newsletterSubscriptions}
            revenueAnalytics={revenueAnalytics}
            siteAnalytics={siteAnalytics}
            wishlistAnalytics={wishlistAnalytics}
            pendingOrdersDesc={pendingOrdersDesc}
            recent5OrdersDesc={recent5OrdersDesc}
            formatPrice={formatPrice}
            formatDate={formatDate}
            getPaymentMethodLabel={getPaymentMethodLabel}
            setCurrentView={setCurrentView}
          />
        )}

        {/* Newsletter Management */}
        {currentView === "newsletter" && (
          <NewsletterTab
            newsletterSubscriptions={newsletterSubscriptions}
            newsletterStats={newsletterStats}
            loading={loading}
            setLoading={setLoading}
            setError={setError}
            setSuccess={setSuccess}
            fetchNewsletterSubscriptions={fetchNewsletterSubscriptions}
          />
        )}

        {/* Shipping Rules Management */}
        {currentView === "shipping" && (
          <ShippingTab
            shippingRulesAdmin={shippingRulesAdmin}
            loading={loading}
            setShowCreateShippingModal={setShowCreateShippingModal}
            fetchShippingRules={fetchShippingRules}
            setSuccess={setSuccess}
            setError={setError}
          />
        )}

        {currentView === "advertising" && <AdvertisingTab setSuccess={setSuccess} setError={setError} />}

        {/* Prevendita Management */}
        {currentView === "preorders" && (
          <PreordersTab
            articles={articles}
            loading={loading}
            setCurrentView={setCurrentView}
          />
        )}

        {/* Prenotazione Management */}
        {currentView === "reservations" && (
          <ReservationsTab
            articles={articles}
            loading={loading}
            setCurrentView={setCurrentView}
          />
        )}

        {/* Reviews Management */}
        {currentView === "reviews" && (
          <ReviewsTab
            reviews={reviews}
            loading={loading}
            fetchReviews={fetchReviews}
            setError={setError}
            setSuccess={setSuccess}
          />
        )}

        {currentView === "promos" && (
          <PromosTab
            coupons={coupons}
            articles={articles}
            fetchCoupons={fetchCoupons}
            editingCoupon={editingCoupon}
            setEditingCoupon={setEditingCoupon}
            showCouponModal={showCouponModal}
            setShowCouponModal={setShowCouponModal}
          />
        )}

        {/* Customers Management Section */}
        {currentView === "customers" && (
          <CustomersTab
            customers={customers}
            customerSearch={customerSearch}
            setCustomerSearch={setCustomerSearch}
            selectedCustomer={selectedCustomer}
            setSelectedCustomer={setSelectedCustomer}
            customerOrders={customerOrders}
            setCustomerOrders={setCustomerOrders}
            fetchCustomerOrders={fetchCustomerOrders}
            fetchCustomers={fetchCustomers}
            formatPrice={formatPrice}
            setSuccess={setSuccess}
            setError={setError}
          />
        )}

        {/* eBay Integration Section */}
        {currentView === "ebay" && <EbayTab />}

        {/* Social Auto-Publish (Facebook + Instagram) */}
        {currentView === "social" && <SocialTab />}

        {/* UTM Link Generator (Marketing tracking) */}
        {currentView === "utm" && <UtmGeneratorTab />}

        {/* AI Chat Conversations (read-only audit/feedback) */}
        {currentView === "ai-chat" && <AiChatConversationsTab />}



        {/* Create Shipping Rule Modal */}
        {showCreateShippingModal && (
          <CreateShippingRuleModal
            onClose={() => setShowCreateShippingModal(false)}
            onSuccess={() => {
              setShowCreateShippingModal(false);
              fetchShippingRules();
              setSuccess("Nuova regola di spedizione creata con successo!");
              setTimeout(() => setSuccess(""), 3000);
            }}
            setError={setError}
          />
        )}

        {/* Create Manual Order Modal */}
        {showCreateOrderModal && (
          <CreateManualOrderModal
            articles={articles}
            initialData={duplicateOrderData}
            onClose={() => { setShowCreateOrderModal(false); setDuplicateOrderData(null); }}
            onSuccess={() => {
              setShowCreateOrderModal(false);
              setDuplicateOrderData(null);
              fetchOrders();
              setSuccess("Ordine manuale creato con successo!");
              setTimeout(() => setSuccess(""), 3000);
            }}
            setError={setError}
          />
        )}

        {/* Analytics Modal (extracted into views/AnalyticsModal.jsx) */}
        <AnalyticsModal
          open={showAnalyticsModal}
          onOpenChange={setShowAnalyticsModal}
          article={selectedArticleForAnalytics}
          data={analyticsData}
          loading={loadingAnalytics}
        />
      </div>
    </div>
  );
};

